import React, {ChangeEvent, useEffect, useState} from "react";
import "@infinite-table/infinite-react/index.css";
import '../../index.css';
import tradeService from "../../services/trade.service";
import VOCSETLayout from "../../views/VOCSETLayout";
import {dropdownItems} from "../../views/dropdownItems";
import {
    APIResponse,
    APIResult,
    Trade,
    vocsetColumns
} from '../../types/APITypes';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import {styled} from '@mui/material/styles';
import {
    InfiniteTable,
    DataSource,
    InfiniteTablePropColumns,
    DataSourcePropCellSelection_MultiCell,
    InfiniteTablePropRowStyle
} from '@infinite-table/infinite-react';
import {
    Button,
    Tabs,
    Tab,
    Dialog,
    DialogContent,
    Typography,
    DialogTitle,
    DialogContentText,
    DialogActions,
    MenuItem,
    Select,
    ButtonGroup
} from "@mui/material";
import {MoonLoader} from "react-spinners";
import AliasService from '../../services/alias.service';
// @ts-ignore
import {CSVLink} from "react-csv";
import {CloudDownload} from "@mui/icons-material";
import {processTradeFile, groupBy} from "./TradeFormatUtils";
import AccountService from "../../services/account.service";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const licenseKey = process.env.REACT_APP_INFINITE_TABLE_LICENSE_KEY;
// @ts-ignore
const tradeColumns: InfiniteTablePropColumns<Trade> = vocsetColumns;
// @ts-ignore
let unvalidatedTradeColumns: InfiniteTablePropColumns<UnvalidatedTrade> = vocsetColumns;

const TradeDashboard = () => {
    const [trades, setTrades] = useState<Trade[]>([]);                          // the list of valid trades to be displayed on the trade blotter.
    // const [unvalidatedTrades, setUnvalidatedTrades] = useState<Trade[]>([]);    // the list of invalid trades imported by the user
    let [importedTrades, setImportedTrades] = useState<Trade[]>([]);            // the list of trades imported by the user (valid and invalid)

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(true);
    const [imported, setImported] = useState(false);
    const [openTradeConfDialog, setTradeConfDialog] = useState(false);
    const [openAliasConfDialog, setOpenAliasConfDialog] = useState(false);
    const [selectedBlotter, setSelectedBlotter] = useState<string>((trades.length > 0 ) ? 'trades':'import');
    const [errorTradeIds, setErrorTradeIds] = useState<string[]>([]);
    const [errorDetails, setErrorDetails] = useState<any[]>([]);
    const [cellSelection, setCellSelection] = useState<DataSourcePropCellSelection_MultiCell>({
        defaultSelection: false,
        selectedCells: [],
    });
    const [goodTradeCount, setGoodTradeCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [entityName, setEntityName] = useState('');
    const [assetClass, setAssetClass] = useState('');
    const [alias, setAlias] = useState('');
    const [value, setValue] = useState('');
    const [columnField, setColumnField] = useState('');
    const [valueOptions, setValueOptions] = useState([]);
    const [contractOptions, setContractOptions] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [aliasError, setAliasError] = useState<string | null>(null);
    const [exchangeMic, setExchangeMic] = useState('');

    useEffect(() => {
        // make sure caches are pre-loaded
        if (!localStorage.getItem('axios-cache-account')) {
            AccountService.getAccounts(false);
        }
        if (!localStorage.getItem('axios-cache-alias')) {
            AliasService.findAliases(false);
        }
        const fetchTrades = async () => {
            setLoading(true);
            try {
                const {result: fetchedTrades} = await tradeService.getTrades();
                setTrades(fetchedTrades || []);
            } catch (error) {
                console.error('Failed to fetch trades:', error);
            } finally {
                setLoading(false);
            }
        };

        if (selectedBlotter === 'trades') fetchTrades();
    }, [selectedBlotter]);

    const handleFile = async (ev: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const fileArrayBuffer = await ev.target.files?.[0]?.arrayBuffer();
        if (!fileArrayBuffer) return;

        setSubmitted(false);
        setErrorTradeIds([]);
        setCellSelection({defaultSelection: false, selectedCells: []});
        let importedFromFile: Trade[] = await processTradeFile(ev.target.files?.[0]?.name, fileArrayBuffer);
        console.log(`Imported ${importedFromFile.length} Trades : `, importedFromFile);
        // @ts-ignore
        setImportedTrades(importedFromFile);
        // @ts-ignore
        //setUnvalidatedTrades(imported);
        let errors = validateClientSide(importedFromFile);
        if (errors.length > 0) {
            console.log(errors);
            const errorTradeIDs = new Set<string>(errors.map(r =>r.id))
            setErrorTradeIds(Array.from(errorTradeIDs.values()));
            setErrorCount(errorTradeIDs.size);
            setErrorDetails(errors);
            setErrorTradeIds(Array.from(errorTradeIDs.values()));
            const cells = errors.map(r => [r.id, r.field]);
            // @ts-ignore
            setCellSelection({defaultSelection: false, selectedCells: cells});
        } else {
            if (importedFromFile.length > 0) {
                console.log("no Errors");
                setImported(true);
            }
            setErrorTradeIds([]);
            setErrorCount(0);
            setErrorDetails([]);
            setErrorTradeIds([]);
            setCellSelection({defaultSelection: false, selectedCells: []});
            ev.target.value = '';
        }
    };


    const handleSubmitTrades = async (event: {
        preventDefault: () => void
    }) => {
        event.preventDefault();
        try {
            setErrorCount(0);
            const response : APIResponse = await tradeService.postNewTrades(importedTrades);
            const result : APIResult[] = response.result as unknown as APIResult[];

            let resultSummary : Record<string, APIResult[]> = groupBy(result, i => i.status);

            const goodTradeStatus = resultSummary['OK'];
            // Remove any errors on tradeId column (this would be for trades already processed).
            // Note that tradeID duplicates within a single file are treated separately (value is prefixed with file:)
            const errorTradeStatus = resultSummary['ERROR']?.filter((r: APIResult) => !(r.field === "tradeID" && r.message?.includes('not unique') && !r.value?.includes("file:")))
            const goodTradeCount = goodTradeStatus?.length|0;

            setGoodTradeCount(goodTradeCount);
            setTradeConfDialog(true);

            if (errorTradeStatus?.length > 0) {
                const errorTradeIDs =  new Set<string>(errorTradeStatus.map(r =>r.id));
                // get the list of all imported trades that are invalid.
                // @ts-ignore
                const invalidTrades =  importedTrades.filter(trade => errorTradeIDs.has(trade.tradeID));
                console.log('invalidTrades1: ', invalidTrades.length);

                setImportedTrades(invalidTrades);
                setErrorCount(invalidTrades.length);
                setErrorDetails(errorTradeStatus);
                setErrorTradeIds(Array.from(errorTradeIDs.values()));
                // @ts-ignore
                const cells = errorTradeStatus.map(r => [r.id, r.field]);
                // @ts-ignore
                setCellSelection({defaultSelection: false, selectedCells: cells});
                console.log('cells: ',cells, 'filteredErr: ', errorTradeStatus,'errTradeID: ',errorTradeIDs);
                setSubmitted(false);
            } else {
                // const invalidTrades = importedTrades
                //     .filter(trade => !result.some(r => r.id === trade.tradeID && r.status === 'OK'));
                // console.log('invalidTrades2: ', invalidTrades.length);
                // setUnvalidatedTrades(updatedUnvalidatedTrades);
                setImportedTrades([]);
                setErrorCount(0);
                setErrorDetails([]);
                setCellSelection({defaultSelection: false, selectedCells: []});
                setErrorTradeIds([]);
                setImported(false);
                setSubmitted(true);
            }
            console.log('good: ', goodTradeStatus, 'error: ', errorTradeStatus,'errCount: ',errorCount);

        } catch (error) {
            console.error("Error submitting trades:", error);
            setSubmitted(false);
        }
    };

    const handleCloseTradeConfDialog = () => {
        setTradeConfDialog(false);
        if (errorCount > 0) {
            setSelectedBlotter('import')
        } else {
            setSelectedBlotter('trades')
        }
    };

    const fetchAliasValues = async (entityName: string, exchangeMic?: string, assetClassFilter?: string, columnName?: string,
                                    client?: string) => {
        try {
            let fetchedData;
            let roleType;

            if (entityName === 'Account' && columnName) {
                if (columnName === 'clearingAccount') {
                    roleType = 'ROLE_CLEARING_ACCOUNT'
                } else if (columnName === 'executingAccount') {
                    roleType = 'ROLE_EXECUTING_ACCOUNT'
                }
                console.log('entity name role', entityName, roleType)
                fetchedData = await AliasService.findAliasValues(entityName, roleType, client);
                console.log('account fetch ', fetchedData)
            } else {
                console.log(entityName)
                fetchedData = await AliasService.findAliasValues(entityName);
                console.log('contract fetch ', fetchedData)
            }
            let filteredData = fetchedData;
            let filteredMic: string | null = null;

            if (entityName === 'Contract') {
                console.log('fetched contract data ', fetchedData)
                filteredData = fetchedData.filter((item: {
                    exchangeMic: string;
                    assetClass: string
                }) =>
                    item.exchangeMic === exchangeMic && item.assetClass === assetClassFilter);

                console.log('filtered contract data ', filteredData)

                if (filteredData.length === 0) {
                    if (exchangeMic != null) {
                        filteredData = fetchedData.filter((item: {
                                micAlias: string[];
                                assetClass: string;
                            }) =>
                                item.micAlias.includes(exchangeMic) && item.assetClass === assetClassFilter
                        );
                    }
                }

                if (filteredData.length > 0) {
                    filteredMic = filteredData[0].exchangeMic;
                    // @ts-ignore
                    setExchangeMic(filteredMic)
                }

                const contractCodes = filteredData.map((item: {
                    contractCode: any
                }) => item.contractCode);
                setContractOptions(contractCodes);
            }

            setValueOptions(filteredData);
        } catch (error) {
            console.error('Error fetching alias values:', error);
            setError('Error fetching alias values. Please try again.');
        }
    };

    useEffect(() => {
        if (errorDetails.length > 0) {
            console.log("Error Details", errorDetails);
        }
    }, [errorDetails]);

    const handleSubmitAlias = async (event: {
        preventDefault: () => void
    }) => {
        event.preventDefault();

        const entityMap: Record<string, string> = {
            'Contract': 'contractCode',
            'Account': 'accountName',
            'Exchange': 'micCode',
            'Executing Broker': 'companyShortName',
            'Clearing Broker': 'companyShortName',
            'Carry Broker': 'companyShortName',
            'Client': 'companyShortName',
        };

        const fieldMap: Record<string, string> = {
            'Contract': 'contractCode',
            'Account': '',
            'Exchange': 'mic',
            'Executing Broker': 'executingBroker',
            'Clearing Broker': 'clearingBroker',
            'Carry Broker': 'carryBroker',
            'Client': 'client',
        };

        let updatedEntityName = entityName;

        if (['Executing Broker', 'Clearing Broker', 'Client', 'Carry Broker'].includes(entityName)) {
            updatedEntityName = 'Company';
        }

        if (entityName === 'Account' && columnField.trim() !== '') {
            fieldMap['Account'] = columnField;
        }

        const fieldVal = entityMap[entityName] || '';
        const fieldName = fieldMap[entityName] || '';

        const aliasData = {
            type: updatedEntityName,
            fieldName,
            alias,
            [fieldVal]: value,
            // @ts-ignore
            exchangeMicCode: entityName === 'Contract' ? exchangeMic : '',
            assetClass: entityName === 'Contract' ? assetClass : ''
        };

        try {
            // @ts-ignore
            await AliasService.createAlias(aliasData);
            handleOpenAliasConfDialog();
            setTimeout(() => {
                handleCloseAliasConfDialog();
            }, 2000);
        } catch (error) {
            // @ts-ignore
            console.error('Error creating alias:', error.response?.data.result || error);
            setAliasError('Error creating alias. Please try again.');
        }
    };

    const handleOpenAliasConfDialog = () => {
        setOpenAliasConfDialog(true);
    };

    const handleCloseAliasConfDialog = () => {
        setOpenAliasConfDialog(false);
        setShowForm(false);
    };

    const rowStyle: InfiniteTablePropRowStyle<Trade> = ({rowInfo}) => {
        if (rowInfo.isGroupRow) return;

        // @ts-ignore
        const tradeID = rowInfo.data?.tradeID;
        const hasError = errorTradeIds.includes(tradeID);

        if (hasError) {
            const errorDetail = errorDetails.find(err => err.id === tradeID && err.field === 'tradeId');
            if (errorDetail) {
                return undefined;
            }
            return {background: 'tomato'};
        }
        return undefined;
    };


    const getCellContextMenuItems = ({data, column}: { data: Partial<Trade> | null; column: any }) => {
        console.log('context: :',column);
        if (!data?.tradeID || !errorTradeIds.includes(data.tradeID)) return [];

        const error = errorDetails.find(err => err.id === data.tradeID && err.field === column.field);

        if (!error) return [];

        const items = [{key: 'errorMessage', label: error.message || 'No errors found'}];
        const nullItems = [{key: 'errorMessage', label: error.message.replace("[null]", "[]") || 'No errors found'}];

        console.log('error :',error);

        if (column.entityName && !error.message.includes("[null]")) {
            fetchAliasValues(
                column.entityName,
                data?.mic || '',
                data?.optionType && data?.strike ? "Option" : "Future",
                column.id,
                data?.client
            );

            items.push({
                key: 'alias',
                label: 'Create Alias',
                // @ts-ignore
                onAction: ({hideMenu}: { hideMenu: () => void }) => {
                    setEntityName(column.entityName);
                    setColumnField(column.field);
                    setValue('');
                    // @ts-ignore
                    setAlias(data?.[column.field] || '');
                    setShowForm(true);
                    hideMenu();
                },
            });
            return items;
        } else if (column.entityName && error.message.includes("[null]")) {
            nullItems.push({
                key: 'er',
                label: 'Value must not be blank'
            });
            return nullItems;
        }
    };


    return (
        <>
            <VOCSETLayout dropdownItems={dropdownItems} style={{
                display: "flex",
                flexFlow: "column",
                flex: 1
            }}>
                <>
                    {selectedBlotter === 'trades' ? (
                        <>
                            <ButtonGroup>
                                <CSVLink data={trades} filename="trades.csv">
                                    <Button component="span" disabled={trades.length <= 0} variant="contained" startIcon={<CloudDownload/>}>
                                        Export Trades
                                    </Button>
                                </CSVLink>
                            </ButtonGroup>
                        </>
                    ) : (
                            <>
                                <ButtonGroup style={{gap: '10px'}}>
                                    <Button component="label" variant="contained" startIcon={<CloudUploadIcon/>}>
                                        Upload file
                                        <VisuallyHiddenInput type="file" onChange={handleFile}/>
                                    </Button>
                                    <Button component="label" variant="contained" disabled={!imported || submitted} onClick={handleSubmitTrades}
                                            startIcon={<CloudDoneIcon/>} color="success">
                                        Send Trades
                                    </Button>
                                </ButtonGroup>
                            </>
                    )}
                </>
                <Tabs
                    value={selectedBlotter}
                    onChange={(event, newValue) => {
                        setSelectedBlotter(newValue);
                    }}
                    aria-label="Blotter Tabs"
                >
                    <Tab label="Trades" value="trades"/>
                    <Tab label="Import" value="import"/>
                </Tabs>

                {loading ? (<MoonLoader color="#282c34"/>) :
                    (selectedBlotter === 'trades' ? (
                        <>
                            <div className="infinite-theme-mode--light"
                                 style={{display: "flex", flexFlow: "column", flex: 1}}>
                                <DataSource<Trade> data={trades} primaryKey="tradeID">
                                    <InfiniteTable<Trade> licenseKey={licenseKey} columns={tradeColumns}/>
                                </DataSource>
                            </div>
                        </>
                    ) : (
                        <div className="infinite-theme-mode--light"
                             style={{display: "flex", flexFlow: "column", flex: 1}}>
                            <DataSource<Trade> data={importedTrades} primaryKey="tradeID"
                                                          cellSelection={cellSelection}
                                                          selectionMode="multi-cell">
                                <InfiniteTable<Trade>
                                    licenseKey={licenseKey}
                                    columns={unvalidatedTradeColumns}
                                    rowStyle={rowStyle}
                                    getCellContextMenuItems={getCellContextMenuItems}
                                />
                            </DataSource>
                        </div>
                    )
                )}
            </VOCSETLayout>

            <Dialog open={openAliasConfDialog} onClose={() => setOpenAliasConfDialog(false)}>
                <DialogContent>
                    <Typography variant="body1">Alias created successfully!</Typography>
                </DialogContent>
            </Dialog>

            <Dialog open={openTradeConfDialog} onClose={handleCloseTradeConfDialog}>
                <DialogTitle>Trade Submission Result</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {goodTradeCount > 0 && ` ${goodTradeCount} trades submitted.`}
                        {errorCount > 0 && ` ${errorCount} trade(s) contained errors.`}
                        {(goodTradeCount == 0 && errorCount == 0) && `All trades uploaded`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTradeConfDialog} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

                {showForm && (
                    <Dialog open={showForm} onClose={() => setShowForm(false)}>
                        <DialogTitle>Create Alias</DialogTitle>
                        <DialogContent>
                            <Select
                                fullWidth
                                id="value"
                                value={value}
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    setValue(selectedValue);
                                    if (entityName === 'Contract') {
                                        // @ts-ignore
                                        const selectedItem = valueOptions.find(item => item.contractCode === selectedValue);
                                        // @ts-ignore
                                        setAssetClass(selectedItem?.assetClass || '');
                                    } else {
                                        setAssetClass('');
                                    }
                                }}
                                displayEmpty
                                sx={{marginTop: '16px'}}
                                disabled={!(entityName === 'Contract' ? contractOptions : valueOptions).length}
                                MenuProps={{PaperProps: {sx: {maxHeight: 350}}}}
                            >
                                {(entityName === 'Contract' ? contractOptions : valueOptions).map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                )) || (
                                    <MenuItem value="">
                                        {entityName === 'Contract' ? 'No contract values available' : 'Select Value'}
                                    </MenuItem>
                                )}
                            </Select>
                            {entityName === 'Contract' && valueOptions.length === 0 && (
                                <Typography variant="body2" color="error" sx={{marginTop: '16px'}}>
                                    Error: Invalid MIC.
                                </Typography>
                            )}
                            {entityName === 'Account' && valueOptions.length === 0 && (
                                <Typography variant="body2" color="error" sx={{marginTop: '16px'}}>
                                    Error: No aliasable Clearing Accounts Found.
                                </Typography>
                            )}
                            {aliasError && (
                                <Typography variant="body2" color="error" sx={{marginTop: '16px'}}>
                                    {aliasError}
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions
                            sx={{
                                justifyContent: (entityName === 'Contract' && !contractOptions.length) ? 'center' : 'flex-end',
                            }}
                        >
                            {!(entityName === 'Contract' && !contractOptions.length) ? (
                                <>
                                    <Button
                                        onClick={handleSubmitAlias}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Submit
                                    </Button>
                                    <Button
                                        onClick={() => setShowForm(false)}
                                        variant="contained"
                                    >
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={() => setShowForm(false)}
                                    variant="contained"
                                >
                                    Cancel
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>
                )}
        </>
    );

};

export default TradeDashboard;

function setError(arg0: string) {
    throw new Error("Function not implemented.");
}

function validateClientSide(trades:Trade[]) : APIResult[] {
    let res : APIResult[] = [];
    let uniqueID: Set<string> = new Set();
    let dups: Set<string> = new Set();
    for (const trd of trades) {
        // if the client and clearing broker are blank, then the clearingAccount lookup failed.
        if (trd.sourceSystem === 'WebICE' || trd.sourceSystem === 'CMEDirect' || trd.sourceSystem === 'ICEBlock') {
            if (trd.client === undefined && trd.clearingBroker == undefined) {
                // @ts-ignore
                let err: APIResult = {};
                err.id = trd.tradeID;
                err.status = 'ERROR';
                err.message = "Invalid clearingAccount [" + trd.clearingAccount + "]";
                err.field = 'clearingAccount';
                err.value = trd.clearingAccount;
                res.push(err);
            }
        }
        if (uniqueID.has(trd.tradeID)) {
            dups.add(trd.tradeID);
        } else {
            uniqueID.add(trd.tradeID);
        }
    }
    console.log('err:',dups);
    dups.forEach(function(id) {
        // @ts-ignore
        let err: APIResult = {};
        err.id = id;
        err.status = 'ERROR';
        err.message = "Duplicate TradeID [" + id + "]";
        err.field = 'tradeID';
        err.value = id;
        console.log('err:',err);
        res.push(err);
    });
    return res;
}
